import type { DocumentNode } from 'graphql'
import { produce } from 'immer'
import camelCase from 'lodash/camelCase'
import omit from 'lodash/omit'
import uniqBy from 'lodash/uniqBy'
import uniqueId from 'lodash/uniqueId'
import type { AnyVariables, DocumentInput, OperationContext, OperationResult, TypedDocumentNode } from 'urql'
import { type StateCreator } from 'zustand'

import type { ApplicationState, BoundedState, MapSliceState } from '@app/store/types'
import type { JSONValue, DomainObject } from '@app/types'
import { actionMutation, makeClient } from '@graphql/client'
import { CommentCreate, CommentDelete } from '@graphql/documents/comment.graphql'
import { DomainObjectsBulkLabel, DomainObjectsDelete } from '@graphql/documents/domain_object.graphql'
import { EntityContainmentCreate, EntityContainmentDelete } from '@graphql/documents/entity_containment.graphql'
import { FavoriteCreate, FavoriteDelete } from '@graphql/documents/favorite.graphql'
import { IntegrationCreate, IntegrationUpdate } from '@graphql/documents/integration.graphql'
import {
  MetricDataPointCreate,
  MetricDataPointDelete,
  MetricDataPointUpdate
} from '@graphql/documents/metric_data_point.graphql'
import { DomainObjectUpdate } from '@graphql/documents/strategy.graphql'
import type {
  CommentCreateMutation,
  DomainObjectsBulkLabelMutation,
  DomainObjectsDeleteMutation,
  IntegrationCreateMutation,
  IntegrationUpdateMutation
} from '@graphql/queries'
import type { Favorite, Label, NodeObjectInput } from '@graphql/types'

export const initialAppState: Omit<
  ApplicationState,
  | 'actionMutation'
  | 'loaderQuery'
  | 'bulkAdd'
  | 'bulkDelete'
  | 'bulkLabel'
  | 'addObject'
  | 'addObjectPage'
  | 'addObjectToPage'
  | 'setObject'
  | 'updateObject'
  | 'updateObjects'
  | 'deleteObject'
  | 'setCurrentUser'
  | 'setFilteredNodeIds'
  | 'favoriteObject'
  | 'unfavoriteObject'
  | 'addComment'
  | 'deleteComment'
  | 'createIntegration'
  | 'updateIntegration'
  | 'updateLabels'
  | 'addEntityContainment'
  | 'deleteEntityContainment'
  | 'addMetricDataPoint'
  | 'deleteMetricDataPoint'
  | 'updateMetricDataPoint'
> = {
  node: {},
  account: {},
  aiAssistantFile: {},
  basicCard: {},
  businessContext: {},
  cardType: {},
  chat: {},
  comment: {},
  commentThread: {},
  correlationPair: {},
  credential: {},
  entity: {},
  favorite: {},
  goal: {},
  googleSheet: {},
  integration: {},
  invitation: {},
  lookerDashboard: {},
  mapImage: {},
  message: {},
  metric: {},
  metricDataPoint: {},
  metricSource: {},
  note: {},
  playbook: {},
  recurringReportConfiguration: {},
  schedule: {},
  section: {},
  slackChannel: {},
  strategy: {},
  task: {},
  user: {},
  pipeline: {},
  currentUser: null,
  page: { basicCard: {}, entity: {}, metric: {}, metricDataPoint: {}, report: {}, strategy: {}, task: {} },
  filteredNodeIds: []
}

// This can go down to just ApplicationState once the get().map call is removed
type AppState = ApplicationState & MapSliceState

type AppSliceFunction = StateCreator<AppState, [['zustand/devtools', 'never']], [], ApplicationState>

// Bust out this function so it can be optimized by the compiler instead of redefined each loop.
const partialObjectEquality = (partialObject, existingObject): boolean =>
  Object.keys(partialObject).some((key) => !Object.is(partialObject[key], existingObject[key]))

export const appSlice: AppSliceFunction = (set, get) => ({
  ...initialAppState,
  actionMutation: <
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    T = any,
    V extends AnyVariables & { input: Record<string, JSONValue> } = { input: Record<string, JSONValue> }
  >(
    mutation: DocumentInput<T, V>,
    input = {},
    context = {},
    variables = {}
  ): Promise<OperationResult<T>> => {
    const client = makeClient(get())
    return client.mutation<T, V>(mutation, { input, ...variables } as V, context).toPromise()
  },
  loaderQuery: <
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    T = any,
    V extends AnyVariables = AnyVariables
  >(
    query: string | DocumentNode | TypedDocumentNode<T, V>,
    variables: V = undefined,
    context: Partial<OperationContext> = {}
  ): Promise<OperationResult<T>> => {
    const client = makeClient(get())

    return client.query<T, V>(query, variables, context).toPromise()
  },
  // This needs to stay distinct from just wrapping addObject. The set/produce call produces a store update that causes
  // re-renders of all listening components. Doing set/produce N times can easily cause the app to freeze.
  bulkAdd: (objects) => {
    set(
      produce((draft) => {
        objects.forEach((object) => {
          const type = camelCase(object.classType)
          const existingObject = draft[type]?.[object.id] || {}

          // only merge update if data has been added or changed
          const dataChanged = partialObjectEquality(object, existingObject)

          if (dataChanged) {
            draft[type][object.id] = { ...existingObject, ...object }
          }
        })
      })
    )
  },
  bulkDelete: async (objects, options = {}) => {
    const groupedObjects = objects.reduce(
      (acc, object) => {
        const [type, ids] = Object.entries(object)[0]

        acc[type] ||= new Set()
        ids.forEach((id) => acc[type].add(id))

        return acc
      },
      {} as Record<string, Set<string | number>>
    )

    let result = null

    if (!options.skipMutation) {
      const inputs = Object.entries(groupedObjects).map(([classType, ids]) => ({ [classType]: [...ids] }))
      result = await get().actionMutation<DomainObjectsDeleteMutation>(DomainObjectsDelete, { objects: inputs })
    }

    set(
      produce((draft) => {
        Object.entries(groupedObjects).forEach(([classType, ids]) => {
          draft[classType] = omit(draft[classType], Array.from(ids))

          if (options.updatePageCounts) {
            const page = draft.page[classType]
            if (page?.metadata) {
              const newCount = (page.metadata?.totalCount || 0) - ids.size
              page.metadata.totalCount = newCount
            }
          }
        })
      })
    )

    return result
  },
  bulkLabel: async (objects, add, remove) => {
    const groupedObjects = objects.reduce(
      (acc, object) => {
        const { id, classType } = object
        const type = camelCase(classType)

        acc[type] ||= new Set()
        acc[type].add(id)

        return acc
      },
      {} as Record<string, Set<string | number>>
    )

    const inputObjects = Object.entries(groupedObjects).map(([classType, ids]) => ({
      [classType]: [...ids]
    }))

    const addedLabels = add.map((name) => ({ name }))

    inputObjects.forEach((inputObject) => {
      const type = Object.keys(inputObject)[0]
      const ids = inputObject[type]
      const removeNames = new Set(remove.map((r) => r.toLowerCase()))
      const typeStore = get()[type]
      const updates = {}

      ids.forEach((id) => {
        const typeStoreObject = typeStore[id]

        if (!typeStoreObject) {
          return
        }

        const mdo = { ...typeStoreObject } // needs to be a new object
        const existingLabels = mdo?.labels || []

        mdo.labels = uniqBy([...addedLabels, ...existingLabels], 'name').filter(
          (label: Label) => !removeNames.has(label.name.toLowerCase())
        )

        updates[id.toString()] = mdo
      })

      set(
        produce((draft) => {
          draft[type] = {
            ...draft[type],
            ...updates
          }
        })
      )
    })

    const input = {
      objects: inputObjects,
      add,
      remove
    }

    return actionMutation<DomainObjectsBulkLabelMutation>(DomainObjectsBulkLabel, input)
  },
  addObject: (object, options = null) => {
    const type = options?.typeOverride || camelCase(object.classType)

    set(
      produce((draft) => {
        const existingObject = draft[type][object.id] || {}

        // only merge update if data has been added or changed
        const dataChanged = partialObjectEquality(object, existingObject)

        if (dataChanged) {
          draft[type][object.id] = { ...existingObject, ...object }
        }
      })
    )
  },
  addObjectPage: (objectType, collection, metadata) => {
    const { bulkAdd } = get()

    const order = collection.map((object) => object.id)

    bulkAdd(collection)

    set(
      produce((draft: Pick<AppState, 'page'>) => {
        draft.page[objectType] = { metadata, order }
      })
    )
  },
  addObjectToPage: (object, index = 'last') => {
    const type = camelCase(object.classType) as keyof BoundedState['page']

    set(
      produce((draft) => {
        const { page } = draft
        const order = page[type]?.order || []

        if (!order.includes(object.id)) {
          if (index === 'last') {
            order.push(object.id)
          } else {
            order.splice(index, 0, object.id)
          }

          const metadata = page[type]?.metadata || {}
          metadata.totalCount ||= 0
          metadata.totalCount += 1

          draft.page[type] = {
            ...page[type],
            metadata,
            order
          }
        }
      })
    )
  },
  updateObject: async (object, skipMutation = false) => get().updateObjects([object], skipMutation),
  updateObjects: async (objects, skipMutation = false) => {
    objects.map(async (object) => {
      const key = Object.keys(object)[0]
      const data = Object.values(object)[0] as DomainObject

      get().addObject({ ...data, classType: data.classType || key })
    })

    if (!skipMutation) {
      const input = { objects }

      // How does error handling get unpacked anymore around specific fields?
      return actionMutation(DomainObjectUpdate, input)
    }

    return new Promise((resolve) => {
      resolve(null)
    })
  },
  deleteObject: async (object, options = {}) => get().bulkDelete([{ [object.classType]: [object.id] }], options),
  setCurrentUser: (currentUser) => {
    set({ currentUser })
  },
  setFilteredNodeIds: (setter) => {
    const filteredNodeIds = setter(get().filteredNodeIds)

    set({ filteredNodeIds })
  },
  favoriteObject: async (object) => {
    const favoritableType = object.classType
    const favoritableId = object.id
    const input = { favoritableType, favoritableId }

    const optimisticUpdateObject = { metric: { ...object, favoriteId: 'optimistic-favorite' } }

    get().updateObject(optimisticUpdateObject, true)

    const resp = await actionMutation(FavoriteCreate, input)

    const favorite = resp?.data?.favoriteCreate?.favorite

    if (favorite) {
      get().addObject(favorite)

      const updatedObject = {} as NodeObjectInput
      updatedObject[favoritableType] = { ...object, favoriteId: favorite.id }
      get().updateObject(updatedObject, true)
    }
  },
  unfavoriteObject: async (object) => {
    const favoritableType = object.classType
    const favoritableId = object.id
    const input = { favoritableType, favoritableId }

    const optimisticUpdateObject = {} as NodeObjectInput
    optimisticUpdateObject[favoritableType] = { ...object, favoriteId: null }

    get().updateObject(optimisticUpdateObject, true)

    set(
      produce((draft) => {
        const favorites: Favorite[] = Object.values(draft.favorite)
        const favoriteId = favorites.find((f) => f.favoritableId === favoritableId)?.id

        if (favoriteId) {
          delete draft.favorite[favoriteId]
        }
      })
    )

    return actionMutation(FavoriteDelete, input)
  },
  addComment: async (comment, creator) => {
    const id = uniqueId('comment-')
    get().addObject({ ...comment, classType: 'comment', createdAt: new Date().toString(), id, creator })

    const resp = await actionMutation<CommentCreateMutation>(CommentCreate, comment)
    const createdComment = resp?.data?.commentCreate?.comment

    if (createdComment) {
      await get().deleteObject({ id, classType: 'comment' }, { skipMutation: true })

      get().addObject(createdComment)
    }
  },
  deleteComment: (comment) => {
    get().deleteObject(comment, { skipMutation: true })

    return actionMutation(CommentDelete, { commentId: comment.id })
  },
  createIntegration: async (attributes) => {
    const response = await actionMutation<IntegrationCreateMutation>(IntegrationCreate, attributes)
    const result = response?.data?.integrationCreate
    const integration = result?.integration

    if (integration) {
      get().addObject(integration)
    }

    return result
  },
  updateIntegration: async (integrationId, attributes) => {
    const response = await actionMutation<IntegrationUpdateMutation>(IntegrationUpdate, {
      integrationId,
      ...attributes
    })
    const result = response?.data?.integrationUpdate
    const integration = result?.integration

    if (integration) {
      get().addObject(integration)
    }

    return result
  },
  updateLabels: async (domainObject, labels = []) => {
    // Optimistically update the object.
    get().addObject({ ...domainObject, labels })

    // Send the mutation. Note: Whatever you pass as labels will be the new labels
    const results = await actionMutation(DomainObjectUpdate, {
      objects: {
        [domainObject.classType]: { id: domainObject.id, labels: labels.map((label) => label.name) }
      }
    })

    // Push the response back on
    const updated = results?.data?.domainObjectUpdate?.nodeObjects[0]?.labels
    get().addObject({ ...domainObject, labels: updated })
  },
  addEntityContainment: (container, entityId) => {
    set(
      produce((draft) => {
        const object = draft[container.classType][container.id]
        const { containeeIds } = object

        draft[container.classType][container.id].containeeIds = [...containeeIds, entityId]
      })
    )

    return actionMutation(EntityContainmentCreate, {
      containerId: container.id,
      containerType: container.classType,
      entityId
    })
  },
  deleteEntityContainment: (container, entityId) => {
    set(
      produce((draft) => {
        const object = draft[container.classType][container.id]
        const containeeIds = object.containeeIds.filter((id) => id !== entityId)

        draft[container.classType][container.id].containeeIds = containeeIds
      })
    )

    return actionMutation(EntityContainmentDelete, {
      containerId: container.id,
      containerType: container.classType,
      entityId
    })
  },
  addMetricDataPoint: async (metricId, date, value, strategyId = null) => {
    const temporaryId = uniqueId('metricDataPoint-')

    set(
      produce((draft) => {
        draft.metricDataPoint[temporaryId] = { metricId, date, value }
      })
    )

    const res = await get().actionMutation(
      MetricDataPointCreate,
      { metricId, date, value: parseFloat(value) },
      {},
      { strategyId }
    )
    const metricDataPoint = res?.data?.metricDataPointCreate?.metricDataPoint

    if (metricDataPoint) {
      set(
        produce((draft) => {
          draft.metricDataPoint[metricDataPoint.id] = metricDataPoint
          draft.page.metricDataPoint.order.unshift(metricDataPoint.id)
          delete draft.metricDataPoint[temporaryId]
        })
      )
    }
  },
  updateMetricDataPoint: (metricDataPointId, date, value, strategyId = null) => {
    set(
      produce((draft) => {
        draft.metricDataPoint[metricDataPointId] = { ...draft.metricDataPoint[metricDataPointId], date, value }
      })
    )

    return get().actionMutation(
      MetricDataPointUpdate,
      {
        metricDataPointId,
        date,
        value: parseFloat(value)
      },
      {},
      { strategyId }
    )
  },
  deleteMetricDataPoint: (metricDataPointId) => {
    set(
      produce((draft) => {
        draft.page.metricDataPoint.order = draft.page.metricDataPoint.order.filter((id) => id !== metricDataPointId)
      })
    )

    get().deleteObject({ classType: 'metricDataPoint', id: metricDataPointId }, { skipMutation: true })

    return get().actionMutation(MetricDataPointDelete, { metricDataPointId })
  }
})
